<template>
  <div>
    <Navigation />
    <div class="wrap">
      <div class="head">
        <div>
          <h1>Layby / Installments</h1>
          <p class="lede">Customers reserve items with a deposit and pay in instalments. Stock stays with them or reserved on the shelf.</p>
        </div>
        <div class="head-actions">
          <select v-model="statusFilter" @change="load" style="max-width:180px">
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button class="primary" @click="openNew">+ New layby</button>
        </div>
      </div>

      <div class="card kpi-strip">
        <div class="kpi"><div class="kpi-label">Active laybys</div><div class="kpi-val">{{ counts.active }}</div></div>
        <div class="kpi"><div class="kpi-label">Outstanding</div><div class="kpi-val">KES {{ format(sums.outstanding) }}</div></div>
        <div class="kpi warn" v-if="counts.overdue > 0"><div class="kpi-label">Overdue</div><div class="kpi-val">{{ counts.overdue }}</div></div>
      </div>

      <div class="card">
        <table v-if="rows.length">
          <thead>
            <tr><th>Receipt</th><th>Customer</th><th>Status</th><th class="r">Total</th><th class="r">Paid</th><th class="r">Balance</th><th>Due</th><th></th></tr>
          </thead>
          <tbody>
            <tr v-for="r in rows" :key="r.id" :class="{ overdue: isOverdue(r) }">
              <td><code>{{ r.receipt_no }}</code></td>
              <td><strong>{{ r.customer_name || '—' }}</strong><div class="muted small">{{ r.customer_phone || '' }}</div></td>
              <td><span :class="'lb-pill ' + r.status">{{ r.status }}</span></td>
              <td class="r">KES {{ format(r.total) }}</td>
              <td class="r">KES {{ format(r.paid_to_date) }}</td>
              <td class="r"><strong>KES {{ format(r.balance) }}</strong></td>
              <td>
                <span v-if="!r.due_date" class="muted small">—</span>
                <span v-else :class="isOverdue(r) ? 'warn' : ''">{{ r.due_date }}<span v-if="isOverdue(r)" class="small"> · {{ Math.abs(r.days_to_due) }}d overdue</span></span>
              </td>
              <td><button class="secondary small" @click="openDetail(r.id)">Open</button></td>
            </tr>
          </tbody>
        </table>
        <p v-else class="muted center">No laybys yet.</p>
      </div>
    </div>

    <!-- New layby modal -->
    <div v-if="showNew" class="modal-back" @click.self="showNew=false">
      <div class="modal card wide">
        <h3>New layby</h3>
        <label>Customer (required)</label>
        <div class="cust-picker">
          <input v-model="custSearch" @input="onCustSearch" placeholder="Search customer by name or phone…" />
          <div v-if="custResults.length" class="cust-results">
            <div v-for="c in custResults" :key="c.id" class="cust-result" @click="pickCustomer(c)">
              <strong>{{ c.name || 'Unnamed' }}</strong> <span class="muted small">· {{ c.phone || 'No phone' }}</span>
            </div>
          </div>
          <div v-if="form.customerName" class="cust-picked">
            <i class="mdi mdi-account-check"></i> {{ form.customerName }}
            <span class="muted small">· {{ form.customerPhone }}</span>
            <button class="secondary small" @click="clearCustomer">Change</button>
          </div>
        </div>

        <label>Items</label>
        <div class="cust-picker">
          <input v-model="varSearch" @input="onVarSearch" placeholder="Search a variant to add…" />
          <div v-if="varResults.length" class="cust-results">
            <div v-for="v in varResults" :key="v.id" class="cust-result" @click="addLine(v)">
              <strong>{{ v.product_name }}</strong> · {{ v.variant_name }}
              <span class="muted small">· KES {{ format(v.sell_price) }}</span>
            </div>
          </div>
        </div>

        <table class="items-mini" v-if="form.items.length">
          <thead><tr><th>Item</th><th style="width:60px">Qty</th><th style="width:100px">Price</th><th class="r">Total</th><th></th></tr></thead>
          <tbody>
            <tr v-for="(it, i) in form.items" :key="i">
              <td>{{ it.product_name }} · {{ it.variant_name }}</td>
              <td><input type="number" v-model.number="it.quantity" min="1" style="width:60px; padding:4px" /></td>
              <td><input type="number" v-model.number="it.unitPrice" min="0" step="0.01" style="width:90px; padding:4px" /></td>
              <td class="r">KES {{ format(it.quantity * it.unitPrice) }}</td>
              <td><button class="danger small" @click="form.items.splice(i,1)">&times;</button></td>
            </tr>
          </tbody>
        </table>

        <div class="row-2" style="margin-top:12px">
          <div><label>Initial deposit (KES)</label><input type="number" v-model.number="form.deposit" min="0" step="0.01" /></div>
          <div><label>Due date</label><input type="date" v-model="form.dueDate" /></div>
        </div>
        <div class="row-2">
          <div><label>Deposit method</label>
            <select v-model="form.depositMethod">
              <option value="cash">Cash</option>
              <option value="mpesa_till">M-Pesa (manual)</option>
              <option value="card">Card</option>
            </select>
          </div>
          <div>
            <label style="text-transform:none; letter-spacing:0; font-weight:500;">
              <input type="checkbox" v-model="form.reserveStock" style="width:auto; margin-right:6px" />
              Reserve stock (removes items from sellable inventory)
            </label>
          </div>
        </div>

        <div class="totals">
          <div>Total: <strong>KES {{ format(subtotal) }}</strong></div>
          <div>Deposit: <strong>KES {{ format(form.deposit) }}</strong></div>
          <div class="balance">Balance: <strong>KES {{ format(subtotal - form.deposit) }}</strong></div>
        </div>

        <div class="modal-actions">
          <button class="secondary" @click="showNew=false">Cancel</button>
          <button class="primary" @click="createLayby" :disabled="creating || !canCreate">{{ creating ? 'Creating…' : 'Create layby' }}</button>
        </div>
        <p class="err" v-if="err">{{ err }}</p>
      </div>
    </div>

    <!-- Detail modal -->
    <div v-if="detail" class="modal-back" @click.self="detail=null">
      <div class="modal card wide">
        <div class="detail-head">
          <div>
            <h3>Layby {{ detail.receipt_no }}</h3>
            <p class="muted">
              {{ detail.customer_name }} · {{ detail.customer_phone }} · <span :class="'lb-pill ' + detail.status">{{ detail.status }}</span>
            </p>
          </div>
          <button class="secondary" @click="detail=null">Close</button>
        </div>
        <div class="kpi-strip" style="padding:0; background:transparent;">
          <div class="kpi"><div class="kpi-label">Total</div><div class="kpi-val">KES {{ format(detail.total) }}</div></div>
          <div class="kpi"><div class="kpi-label">Paid</div><div class="kpi-val">KES {{ format(detail.paid_to_date) }}</div></div>
          <div class="kpi warn" v-if="detail.balance > 0"><div class="kpi-label">Balance</div><div class="kpi-val">KES {{ format(detail.balance) }}</div></div>
        </div>

        <h4>Items</h4>
        <table>
          <thead><tr><th>Item</th><th class="r">Qty</th><th class="r">Price</th><th class="r">Total</th></tr></thead>
          <tbody>
            <tr v-for="it in detail.items" :key="it.id">
              <td>{{ it.variant_name_snapshot }}<div class="muted small">{{ it.sku_snapshot }}</div></td>
              <td class="r">{{ Math.round(it.quantity) }}</td>
              <td class="r">KES {{ format(it.unit_price) }}</td>
              <td class="r">KES {{ format(it.line_total) }}</td>
            </tr>
          </tbody>
        </table>

        <h4>Payments</h4>
        <table>
          <thead><tr><th>When</th><th>Method</th><th>Reference</th><th class="r">Amount</th></tr></thead>
          <tbody>
            <tr v-for="p in detail.payments" :key="p.id">
              <td class="muted small">{{ p.created_at }}</td>
              <td>{{ p.method }}</td>
              <td class="muted">{{ p.reference || '—' }}</td>
              <td class="r" :class="p.amount < 0 ? 'warn' : ''">KES {{ format(p.amount) }}</td>
            </tr>
          </tbody>
        </table>

        <div v-if="detail.status === 'active'" class="detail-actions">
          <div class="pay-row">
            <label>Amount</label>
            <input type="number" v-model.number="payAmount" :max="detail.balance" min="0.01" step="0.01" style="max-width:150px" />
            <label>Method</label>
            <select v-model="payMethod" style="max-width:150px">
              <option value="cash">Cash</option>
              <option value="mpesa_till">M-Pesa</option>
              <option value="card">Card</option>
            </select>
            <label>Reference</label>
            <input v-model="payRef" placeholder="Optional" style="max-width:180px" />
            <button class="primary" @click="submitPayment" :disabled="paying">{{ paying ? 'Recording…' : 'Record payment' }}</button>
          </div>
          <div style="margin-top:14px; display: flex; gap: 8px;">
            <button class="danger" @click="openCancel">Cancel layby</button>
          </div>
        </div>
        <p class="err" v-if="detailErr">{{ detailErr }}</p>
        <p class="ok"  v-if="detailOk">{{ detailOk }}</p>
      </div>
    </div>

    <!-- Cancel modal -->
    <div v-if="showCancel" class="modal-back" @click.self="showCancel=false">
      <div class="modal card">
        <h3>Cancel layby</h3>
        <label>Reason (required)</label>
        <input v-model="cancelForm.reason" placeholder="e.g. Customer no longer wants item" />
        <label>Refund amount (from deposit)</label>
        <input type="number" v-model.number="cancelForm.refundAmount" :max="detail.paid_to_date" min="0" step="0.01" />
        <p class="muted small">Stock will be returned to sellable inventory if it was reserved.</p>
        <div class="modal-actions">
          <button class="secondary" @click="showCancel=false">Nope, keep it</button>
          <button class="danger" @click="submitCancel" :disabled="cancelling">{{ cancelling ? 'Cancelling…' : 'Confirm cancel' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Navigation from '@/components/paysol-nav/Navigation.vue';
import { paysol, execute } from '@/api';

let custTimer = null, varTimer = null;

export default {
  name: 'LaybyManagement',
  components: { Navigation },
  data() {
    return {
      rows: [], statusFilter: 'active',
      showNew: false, creating: false, err: '',
      form: { customerId: 0, customerName: '', customerPhone: '', items: [], deposit: 0, depositMethod: 'cash', dueDate: '', reserveStock: true },
      custSearch: '', custResults: [], varSearch: '', varResults: [],
      // detail
      detail: null, detailErr: '', detailOk: '',
      payAmount: 0, payMethod: 'cash', payRef: '', paying: false,
      showCancel: false, cancelForm: { reason: '', refundAmount: 0 }, cancelling: false,
    };
  },
  computed: {
    subtotal() { return this.form.items.reduce((s, it) => s + (it.quantity * it.unitPrice), 0); },
    canCreate() { return this.form.customerId > 0 && this.form.items.length > 0 && this.form.deposit <= this.subtotal; },
    counts() {
      const active = this.rows.filter(r => r.status === 'active').length;
      const overdue = this.rows.filter(r => this.isOverdue(r)).length;
      return { active, overdue };
    },
    sums() {
      const outstanding = this.rows.filter(r => r.status === 'active').reduce((s, r) => s + Number(r.balance || 0), 0);
      return { outstanding };
    }
  },
  async mounted() { await this.load(); },
  methods: {
    format(n) { return Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    isOverdue(r) { return r.status === 'active' && r.due_date && Number(r.days_to_due) < 0; },
    async load() {
      const { data } = await execute({ function: 'listLaybys', status: this.statusFilter }, paysol);
      if (data.success) this.rows = data.data;
    },
    openNew() {
      this.form = { customerId: 0, customerName: '', customerPhone: '', items: [], deposit: 0, depositMethod: 'cash', dueDate: '', reserveStock: true };
      this.custSearch = ''; this.custResults = []; this.varSearch = ''; this.varResults = []; this.err = '';
      this.showNew = true;
    },
    onCustSearch() {
      clearTimeout(custTimer);
      const q = this.custSearch.trim();
      if (q.length < 2) { this.custResults = []; return; }
      custTimer = setTimeout(async () => {
        const { data } = await execute({ function: 'listCustomers', search: q }, paysol);
        if (data.success) this.custResults = data.data.slice(0, 8);
      }, 300);
    },
    pickCustomer(c) {
      this.form.customerId = c.id;
      this.form.customerName = c.name || 'Unnamed';
      this.form.customerPhone = c.phone || '';
      this.custResults = []; this.custSearch = '';
    },
    clearCustomer() { this.form.customerId = 0; this.form.customerName = ''; this.form.customerPhone = ''; },
    onVarSearch() {
      clearTimeout(varTimer);
      const q = this.varSearch.trim();
      if (q.length < 2) { this.varResults = []; return; }
      varTimer = setTimeout(async () => {
        const { data } = await execute({ function: 'listProducts', search: q }, paysol);
        if (!data.success) return;
        const out = [];
        for (const p of data.data.slice(0, 5)) {
          const { data: pd } = await execute({ function: 'getProduct', id: p.id }, paysol);
          if (pd.success) for (const v of pd.data.variants || []) {
            out.push({ id: v.id, product_name: p.name, variant_name: v.variant_name, sell_price: v.sell_price });
            if (out.length >= 10) break;
          }
          if (out.length >= 10) break;
        }
        this.varResults = out;
      }, 300);
    },
    addLine(v) {
      if (this.form.items.some(i => i.variantId === v.id)) return;
      this.form.items.push({
        variantId: v.id, product_name: v.product_name, variant_name: v.variant_name,
        quantity: 1, unitPrice: parseFloat(v.sell_price) || 0,
      });
      this.varSearch = ''; this.varResults = [];
    },
    async createLayby() {
      this.creating = true; this.err = '';
      try {
        const payload = {
          function: 'createLayby',
          customerId: this.form.customerId,
          items: this.form.items.map(it => ({ variantId: it.variantId, quantity: it.quantity, unitPrice: it.unitPrice })),
          deposit: this.form.deposit,
          depositMethod: this.form.depositMethod,
          dueDate: this.form.dueDate,
          reserveStock: this.form.reserveStock ? 1 : 0,
        };
        const { data } = await execute(payload, paysol);
        if (!data.success) { this.err = data.message; return; }
        this.showNew = false;
        await this.load();
      } catch (e) { this.err = e.response?.data?.message || 'Create failed.'; }
      finally { this.creating = false; }
    },
    async openDetail(id) {
      const { data } = await execute({ function: 'getLayby', id }, paysol);
      if (data.success) {
        this.detail = data.data;
        this.payAmount = Math.min(Number(this.detail.balance), Number(this.detail.balance));
        this.payMethod = 'cash'; this.payRef = ''; this.detailErr = ''; this.detailOk = '';
      }
    },
    async submitPayment() {
      this.paying = true; this.detailErr = ''; this.detailOk = '';
      try {
        const { data } = await execute({ function: 'payLayby', id: this.detail.id, amount: this.payAmount, method: this.payMethod, reference: this.payRef }, paysol);
        if (!data.success) { this.detailErr = data.message; return; }
        this.detailOk = data.message;
        await this.openDetail(this.detail.id);
        await this.load();
      } catch (e) { this.detailErr = e.response?.data?.message || 'Failed.'; }
      finally { this.paying = false; }
    },
    openCancel() { this.cancelForm = { reason: '', refundAmount: 0 }; this.showCancel = true; },
    async submitCancel() {
      this.cancelling = true;
      try {
        const { data } = await execute({ function: 'cancelLayby', id: this.detail.id, reason: this.cancelForm.reason, refundAmount: this.cancelForm.refundAmount }, paysol);
        if (!data.success) { alert(data.message); return; }
        this.showCancel = false; this.detail = null; await this.load();
      } finally { this.cancelling = false; }
    },
  }
};
</script>

<style scoped>
.wrap { max-width: 1300px; margin: 0 auto; padding: 24px; }
.head { display: flex; justify-content: space-between; align-items: end; margin-bottom: 20px; gap: 20px; }
h1 { font-size: 26px; margin: 0 0 4px; color: var(--paysol-text); }
.lede { color: var(--paysol-muted); margin: 0; max-width: 600px; }
.head-actions { display: flex; gap: 8px; align-items: center; }
.kpi-strip { display: flex; gap: 12px; padding: 14px; margin-bottom: 12px; }
.kpi { flex: 1; padding: 12px 16px; background: var(--paysol-bg-elev); border-left: 3px solid var(--paysol-teal); border-radius: 6px; }
.kpi.warn { border-left-color: var(--paysol-warning); }
.kpi-label { color: var(--paysol-muted); font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
.kpi-val { color: var(--paysol-text); font-size: 22px; font-weight: 700; margin-top: 3px; }
.r { text-align: right; }
.muted { color: var(--paysol-muted); }
.small { font-size: 11px; }
.warn { color: var(--paysol-warning); }
.ok { color: var(--paysol-success); }
.center { text-align: center; padding: 40px 0; }
.err { color: var(--paysol-danger); font-size: 12px; margin-top: 8px; }
code { background: var(--paysol-bg-elev); padding: 2px 6px; border-radius: 3px; color: var(--paysol-teal); font-size: 11px; }
button.small { padding: 4px 10px; font-size: 11px; }
tr.overdue td:first-child { border-left: 2px solid var(--paysol-warning); }
.lb-pill { display: inline-block; padding: 3px 8px; border-radius: 3px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
.lb-pill.active    { background: rgba(79,195,247,.15); color: #4FC3F7; }
.lb-pill.completed { background: rgba(16,185,129,.15); color: var(--paysol-success); }
.lb-pill.cancelled { background: rgba(239,68,68,.15); color: var(--paysol-danger); }
.modal-back { position: fixed; inset: 0; background: rgba(0,0,0,0.6); display: flex; align-items: center; justify-content: center; z-index: 50; padding: 24px; }
.modal { width: 100%; max-width: 500px; max-height: 90vh; overflow-y: auto; }
.modal.wide { max-width: 860px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--paysol-line); }
.cust-picker { position: relative; }
.cust-results { position: absolute; z-index: 5; background: var(--paysol-card); border: 1px solid var(--paysol-line); border-radius: 6px; margin-top: 4px; max-height: 220px; overflow-y: auto; width: 100%; box-shadow: 0 8px 20px rgba(0,0,0,.15); }
.cust-result { padding: 8px 12px; cursor: pointer; border-bottom: 1px solid var(--paysol-line); }
.cust-result:hover { background: var(--paysol-teal-glow); }
.cust-picked { display: flex; align-items: center; gap: 8px; margin-top: 6px; padding: 8px 12px; background: var(--paysol-teal-glow); border-radius: 6px; color: var(--paysol-teal); font-weight: 500; }
.cust-picked i { font-size: 18px; }
.cust-picked button { margin-left: auto; }
.row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.items-mini { margin: 10px 0; }
.totals { display: flex; justify-content: flex-end; gap: 20px; margin-top: 10px; padding-top: 10px; border-top: 1px solid var(--paysol-line); font-size: 13px; }
.totals .balance { color: var(--paysol-teal); font-size: 16px; }
.detail-head { display: flex; justify-content: space-between; align-items: start; margin-bottom: 16px; }
.detail-head h3 { margin: 0 0 4px; color: var(--paysol-teal); }
h4 { color: var(--paysol-muted); font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; margin: 16px 0 6px; }
.detail-actions { margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--paysol-line); }
.pay-row { display: flex; gap: 8px; align-items: end; flex-wrap: wrap; }
.pay-row label { margin: 0; }
</style>
